import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import { PropsData } from "../../../context";
import { Spinner } from "react-bootstrap";
import {
  Dropdown,
  Modal,
  Input,
  Typography,
} from "@virkesborsen/ui-component-library";
import DFRSummaryCard from "../components/DFRProductCard";
import OrderSummary from "../components/OrderSummary";
import { InvalidValueError } from "@google/maps/lib/internal/validate";

const ProductSelectionStep = ({
  checkoutData,
  setCheckoutData,
  couponCode,
  setCouponCode,
  fetchPrices,
  isLoading,
  isError,
  isLargeProperty,
  hasValidData,
  error,
}) => {
  const propsContext = useContext(PropsData);
  const [selectedProperty, setSelectedProperty] = useState(
    propsContext.props.find((p) => p)
  );

  const properties = propsContext.props.filter(
    (prop) => prop.type === "PROP" && prop.status === "data" && !prop.dfr
  );

  useEffect(() => {
    if (selectedProperty?.ref || couponCode) {
      fetchPrices(selectedProperty, couponCode);
    }
  }, [selectedProperty?.ref, couponCode]);

  const handlePropertyChange = (value) => {
    const propertyData = properties.find((prop) => prop.ref === value);
    setSelectedProperty(propertyData);
  };

  const handleCouponCodeChange = (value) => {
    setCouponCode(value);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    if (isLargeProperty) {
      return (
        <PaperContainer>
          <div className="d-flex justify-content-center align-items-center w-100 h-100">
            <Typography variant="labelMediumMediumDense" color="cloudy-900">
              Fastigheten är större än 1 000 hektar
              <br />
              Eftersom fastigheten överstiger 1 000 hektar behöver vi ta kontakt
              med dig direkt för att kunna ge dig en korrekt offert och ett
              riktigt bra pris. Klicka på Slutför beställning så hör vi av oss
              till dig så snart som möjligt.
            </Typography>
          </div>
        </PaperContainer>
      );
    }

    if (isError) {
      return (
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
          <Typography variant="labelMediumMediumDense" color="cloudy-900">
            {error ||
              "Oj, något gick fel. Vänligen stäng dialogen och försök igen."}{" "}
            <br />
            {"Om felet kvarstår, kontakta oss på info@virkesborsen.se"}
          </Typography>
        </div>
      );
    }

    if (hasValidData) {
      return <OrderSummary checkoutData={checkoutData} />;
    }

    return null;
  };

  if (!selectedProperty) {
    return (
      <Modal.Content bgColor="stone-50">
        <ContentContainer>
          <Dropdown
            name="property"
            label="Välj fastighet"
            value={selectedProperty?.ref}
            options={properties.map((prop) => ({
              label: prop.designation,
              value: prop.ref,
            }))}
            onChange={handlePropertyChange}
            required="true"
            placeholder={
              "Du måste lägga till en fastighet innan du kan gå vidare."
            }
            idleText="Välj en fastighet"
            errorText={
              "Stäng denna ruta och klicka på 'Karta' i menyn,  sedan 'Lägg till fastighet' högst upp på kartan."
            }
            isValid={Boolean(selectedProperty)}
          />
        </ContentContainer>
      </Modal.Content>
    );
  }

  return (
    <Modal.Content bgColor="stone-50">
      <ContentContainer>
        <Dropdown
          name="property"
          label="Välj fastighet"
          value={selectedProperty?.ref}
          options={properties.map((prop) => ({
            label: prop.designation,
            value: prop.ref,
          }))}
          onChange={handlePropertyChange}
          required="true"
        />

        <Input
          description=""
          errorText="Rabattkod är ogiltlig"
          idleText="Skriv in rabattkod"
          label="Lägg till rabattkod"
          name="rabattkod"
          value={couponCode}
          onChange={handleCouponCodeChange}
          successText="Rabattkod tillagd"
          type="text"
          validation={() => !isError}
          isInvalid={isError && couponCode}
        />

        <DFRSummaryCard
          property={selectedProperty}
          area={checkoutData.property?.area}
        />

        {renderContent()}
      </ContentContainer>
    </Modal.Content>
  );
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
  padding-bottom: 20px;
`;

const PaperContainer = styled.div`
  background-color: white;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid #e6e6e8;
`;

ProductSelectionStep.displayName = "ProductSelection";

export default ProductSelectionStep;
